import { css, cx } from '@linaria/atomic';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { ContentLoader, TextField, BannerNotification, Text } from '@frontend/design-system';
import { usePairS710Terminal } from './use-pair-s710-terminal';

const styles = {
  flexColumnCenter: css`
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  successContainer: css`
    row-gap: var(--fabric-space-gap-md);
    column-gap: var(--fabric-space-gap-md);
    border-top: 1px solid var(--fabric-color-green-20);
    border-right: 1px solid var(--fabric-color-green-20);
    border-bottom: 1px solid var(--fabric-color-green-20);
    border-left: 1px solid var(--fabric-color-green-20);
    background: var(--fabric-color-green-5);
    border-top-left-radius: var(--fabric-geometry-standard-radius-md);
    border-top-right-radius: var(--fabric-geometry-standard-radius-md);
    border-bottom-right-radius: var(--fabric-geometry-standard-radius-md);
    border-bottom-left-radius: var(--fabric-geometry-standard-radius-md);
    padding-top: var(--fabric-space-gap-lg);
    padding-right: var(--fabric-space-gap-xl);
    padding-bottom: var(--fabric-space-gap-lg);
    padding-left: var(--fabric-space-gap-xl);
    height: 150px;
  `,
  greenText: css`
    color: var(--fabric-color-green-70);
  `,
  fieldsContainer: css`
    display: flex;
    flex-direction: column;
    row-gap: var(--fabric-space-gap-md);
    column-gap: var(--fabric-space-gap-md);
  `,
  errorBanner: css`
    margin-top: var(--fabric-space-padding-md);
    max-width: fit-content;
  `,
};

type RegisterTerminalBodyProps = {
  listClassName: string;
  isLoading: boolean;
  getFieldProps: ReturnType<typeof usePairS710Terminal>['getFieldProps'];
  error: string | null;
  showStatus: boolean;
};
export const RegisterTerminalBody = ({
  listClassName,
  isLoading,
  getFieldProps,
  error,
  showStatus,
}: RegisterTerminalBodyProps) => {
  const { t } = useTranslation('payments');

  if (showStatus && !error) {
    return (
      <div className={cx(styles.flexColumnCenter, styles.successContainer)}>
        <div className={styles.flexColumnCenter}>
          <Icon name='mark-done' color='success' />
          <Text size='medium' className={styles.greenText}>
            {t('Terminal connected')}
          </Text>
        </div>
        <Text size='large' weight='bold'>
          {t('You are now ready to collect payments!')}
        </Text>
      </div>
    );
  }
  return (
    <div>
      <ContentLoader show={isLoading} message={t('Registering terminal')} />

      <ol className={listClassName}>
        <li>{t('Select Language, then Country, if applicable.')}</li>
        <li>{t('Wait for Terminal to update.')}</li>
        <li>{t('Enter the three words shown on the terminal screen below, then give the terminal a name.')}</li>
      </ol>

      <div className={styles.fieldsContainer}>
        <TextField
          helperText='e.g. apple-river-stone'
          label={t('Registration code')}
          {...getFieldProps('pairingCode')}
        />
        <TextField helperText='e.g. Front desk' label={t('Terminal name')} {...getFieldProps('terminalName')} />
      </div>

      {error && (
        <BannerNotification
          className={styles.errorBanner}
          status='error'
          message={t('There was a problem registering your terminal. Please try again.')}
        />
      )}
    </div>
  );
};
